import { useCallback, useEffect, useState } from "react";
import { getLibraryLessons } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import TeacherLessonLibraryPage from "./TeacherLessonLibraryPage";
import TeacherCommunityLessonsPage from "./TeacherCommunityLessonsPage";
import { TeacherTabStrip } from "./TeacherTabStrip";
import { useUrlState } from "./useUrlState";

const TABS = [
  { id: "mine", label: "My lessons" },
  { id: "drafts", label: "Drafts" },
  { id: "community", label: "Community" },
] as const;

export default function TeacherLessonsPage() {
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const [tab, setTab] = useUrlState("tab", "mine");
  const active = tab === "community" ? "community" : tab === "drafts" ? "drafts" : "mine";
  const [counts, setCounts] = useState({ mine: 0, drafts: 0 });

  const refreshCounts = useCallback(async () => {
    if (!bearer) return;
    try {
      const [mine, drafts] = await Promise.all([
        getLibraryLessons(bearer, { limit: 1 }),
        getLibraryLessons(bearer, { limit: 1, drafts: true }),
      ]);
      setCounts({ mine: mine.page.total, drafts: drafts.page.total });
    } catch {
      setCounts({ mine: 0, drafts: 0 });
    }
  }, [bearer]);

  useEffect(() => {
    void refreshCounts();
  }, [refreshCounts]);

  const notifyLibraryChanged = useCallback(() => {
    void refreshCounts();
  }, [refreshCounts]);

  const tabs = TABS.map((t) => ({
    ...t,
    ...(t.id === "mine" ? { count: counts.mine } : t.id === "drafts" ? { count: counts.drafts } : {}),
  }));

  return (
    <div className="teacher-page submissions-dashboard">
      <header className="submissions-hero overview-hero">
        <div className="submissions-hero-text">
          <p className="eyebrow">Teacher · Lessons</p>
          <h1 className="teacher-page-title">Lessons</h1>
          <p className="teacher-page-lede">
            Your library, in-progress drafts, and lessons shared by the community. Search, browse, and add them to
            any course.
          </p>
        </div>
      </header>
      <TeacherTabStrip tabs={tabs} active={active} onSelect={setTab} ariaLabel="Lesson views" />
      {active === "community" ? (
        <TeacherCommunityLessonsPage embedded />
      ) : (
        <TeacherLessonLibraryPage
          embedded
          scope={active === "drafts" ? "drafts" : "library"}
          onLibraryChanged={notifyLibraryChanged}
        />
      )}
    </div>
  );
}
